import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card } from "../components/Shared";
import { Badge } from "../components/Shared";
import { ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SupervisorVersions() {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/agents").then(({ data }) => {
      const list = Array.isArray(data) ? data : (data?.items || []);
      setAgents(list);
      if (list.length && !agentId) setAgentId(list[0].id);
    });
  }, []);

  const load = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/supervisor/agents/${id}/versions`);
      setData(data);
    } catch { setData(null); } finally { setLoading(false); }
  };

  useEffect(() => { load(agentId); }, [agentId]);

  const rollback = async (version) => {
    setBusy(String(version));
    try {
      await api.post(`/supervisor/agents/${agentId}/rollback`, { target_version: version, reason: "Rollback manual desde panel" });
      toast.success(`Rollback a v${version} aplicado`);
      load(agentId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error en rollback"); } finally { setBusy(""); }
  };

  const versions = data?.versions || [];

  return (
    <>
      <PageHeader label="Inteligencia Conversacional" title="Versiones de agentes"
        description="Historial de configuración de cada agente. Rollback inmediato a una versión anterior.">
        <select data-testid="ver-agent-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}
          className="bg-white border border-carbon-200 rounded px-3 py-2 text-sm min-w-[220px]">
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </PageHeader>
      <PageBody>
        {loading ? <div className="text-carbon-500">Cargando...</div> : versions.length === 0 ? (
          <Card className="p-10 text-center text-carbon-500">Sin versiones para este agente.</Card>
        ) : (
          <div className="space-y-3" data-testid="ver-list">
            {versions.map((v) => (
              <Card key={v.id} className="p-5" testid={`ver-${v.version}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg">v{v.version}</span>
                    <Badge tone={v.status === "PRODUCTION" ? "forest" : v.status === "CANDIDATE" ? "warning" : "neutral"}>{v.status}</Badge>
                    <Badge tone="neutral">{v.source}</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-carbon-400">{v.created_at ? new Date(v.created_at).toLocaleString() : ""} · {v.created_by}</span>
                    {v.status !== "PRODUCTION" && (
                      <button data-testid={`ver-rollback-${v.version}`} onClick={() => rollback(v.version)} disabled={busy}
                        className="bg-white border border-carbon-200 text-carbon-800 px-3 py-1.5 rounded text-xs flex items-center gap-1.5 hover:border-terracotta disabled:opacity-60">
                        <ArrowCounterClockwise size={13} /> Restaurar
                      </button>
                    )}
                  </div>
                </div>
                <pre className="bg-page border border-carbon-200 rounded p-3 text-xs whitespace-pre-wrap font-mono max-h-40 overflow-auto">{(v.prompt || "").slice(0, 1200)}</pre>
              </Card>
            ))}
          </div>
        )}
      </PageBody>
    </>
  );
}
